import { useCallback, useState } from "react"
import type { PopoverOrigin } from "@mui/material"

interface DropdownPosition {
    anchorOrigin: PopoverOrigin
    transformOrigin: PopoverOrigin
}

interface UseDropdownPositionReturn {
    menuPosition: DropdownPosition
    calculatePosition: (event: React.SyntheticEvent<Element>) => void
}

const DROPDOWN_MAX_HEIGHT = 300
const MARGIN = 8

/**
 * Custom hook to calculate optimal dropdown position (above or below)
 * based on available viewport space without overlapping the trigger element.
 *
 * @param maxHeight - Maximum height of the dropdown menu (default: 300)
 * @returns menuPosition and calculatePosition function to call on Select open
 */
export function useDropdownPosition(maxHeight: number = DROPDOWN_MAX_HEIGHT): UseDropdownPositionReturn {
    const [menuPosition, setMenuPosition] = useState<DropdownPosition>({
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
    })

    const calculatePosition = useCallback(
        (event: React.SyntheticEvent<Element>) => {
            const target = event.currentTarget as HTMLElement
            if (!target) return

            const rect = target.getBoundingClientRect()
            const viewportHeight = window.innerHeight
            const spaceBelow = viewportHeight - rect.bottom - MARGIN
            const spaceAbove = rect.top - MARGIN

            // Determine if dropdown should open above or below
            const shouldOpenAbove = spaceBelow < maxHeight && spaceAbove > spaceBelow

            if (shouldOpenAbove) {
                setMenuPosition({
                    anchorOrigin: { vertical: "top", horizontal: "left" },
                    transformOrigin: { vertical: "bottom", horizontal: "left" },
                })
            } else {
                setMenuPosition({
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                })
            }
        },
        [maxHeight]
    )

    return { menuPosition, calculatePosition }
}

/**
 * Standalone function to get MenuProps with dynamic positioning.
 * Use this when you need a one-time calculation without state.
 *
 * @param anchorEl - The anchor element (Select field)
 * @param maxHeight - Maximum height of the dropdown menu
 * @returns MenuProps object for MUI Select
 */
export function getDropdownMenuProps(anchorEl: HTMLElement | null, maxHeight: number = DROPDOWN_MAX_HEIGHT) {
    if (!anchorEl) {
        return {
            anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
            transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
            PaperProps: { style: { maxHeight, marginTop: MARGIN } },
        }
    }

    const rect = anchorEl.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const spaceBelow = viewportHeight - rect.bottom - MARGIN
    const spaceAbove = rect.top - MARGIN

    const shouldOpenAbove = spaceBelow < maxHeight && spaceAbove > spaceBelow

    return {
        anchorOrigin: {
            vertical: shouldOpenAbove ? ("top" as const) : ("bottom" as const),
            horizontal: "left" as const,
        },
        transformOrigin: {
            vertical: shouldOpenAbove ? ("bottom" as const) : ("top" as const),
            horizontal: "left" as const,
        },
        PaperProps: {
            style: {
                maxHeight: Math.min(maxHeight, shouldOpenAbove ? spaceAbove : spaceBelow),
                marginTop: shouldOpenAbove ? -MARGIN : MARGIN,
            },
        },
    }
}

export default useDropdownPosition
